// /src/authContext.js

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { authSocket } from "../utils/Socket";
import { attachAuthListeners } from "../utils/wsListeners/authListeners";
import { startHeartbeat, stopHeartbeat } from "./wsRecovery/heartbeat";
import { set } from "date-fns";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

function ts() {
  return new Date().toISOString();
}

let lastOnlineTs = null;

window.addEventListener("online", () => {
  lastOnlineTs = Date.now();
  //console.log(`[${new Date().toISOString()}][AUTH DEBUG] navigator online (t=0ms)`);
});

function delta() {
  if (!lastOnlineTs) return "n/a";
  return `${Date.now() - lastOnlineTs}ms since online`;
}

export function AuthProvider({ children, preloadedUser = null }) {
  const socketRef = useRef(null);

  // -------------------------------------------------------------
  // preload initial user state
  // -------------------------------------------------------------
  const [isLoggedIn, setIsLoggedIn] = useState(preloadedUser?.isLoggedIn ?? false);
  const [email, setEmail] = useState(preloadedUser?.email ?? null);
  const [discordName, setDiscordName] = useState(preloadedUser?.discordName ?? null);
  const [discordId, setDiscordId] = useState(preloadedUser?.discordId ?? null);
  const [hasMfa, setHasMfa] = useState(preloadedUser?.hasMfa ?? false);
  const [mfaType, setMfaType] = useState(preloadedUser?.mfaType ?? null);
  const [handles, setHandles] = useState(preloadedUser?.handles ?? null);
  const [language, setLanguage] = useState(preloadedUser?.language ?? null);
  const [timezone, setTimezone] = useState(preloadedUser?.timezone ?? null);
  const [lastAcceptanceDate, setLastAcceptanceDate] = useState(preloadedUser?.lastAcceptanceDate ?? null);
  const [gcuAccepted, setGcuAccepted] = useState(preloadedUser?.gcuAccepted ?? false);
  const [rankImageUrl, setRankImageUrl] = useState(preloadedUser?.rankImageUrl ?? null);
  const [rankColors, setRankColors] = useState(preloadedUser?.rankColors ?? null);
  const [userId, setUserId] = useState(preloadedUser?.userId ?? null);
  const [isAltAccount, setIsAltAccount] = useState(preloadedUser?.isAltAccount ?? false);
  const [wsConnected, setWsConnected] = useState(false);

  // -------------------------------------------------------------
  // manual connect() only when user is logged in
  // -------------------------------------------------------------
  const connectWs = () => {
    const socket = socketRef.current;
    if (!socket) return;
    if (!isLoggedIn) return; // protect WS from anonymous users

    if (!socket.connected) {
      //console.log(`[${ts()}][Auth WS] Manual connect()`);
      socket.connect();
      startHeartbeat(socket);
    }
  };

  const disconnectWs = () => {
    const socket = socketRef.current;
    if (!socket) return;

    if (socket.connected) {
      //console.log(`[${ts()}][Auth WS] Manual disconnect()`);
      socket.disconnect();
      stopHeartbeat(socket);
    }
  };

  // -------------------------------------------------------------
  // init WS object + listeners (but no connection yet)
  // -------------------------------------------------------------
  useEffect(() => {
    const socket = authSocket;
    socketRef.current = socket;

    //console.log(`[${ts()}][Auth WS] attachAuthListeners()`);
    const detach = attachAuthListeners(socket, { connectWs, disconnectWs });

    const handleConnect = () => {
      setWsConnected(true);
      //console.log(`[${ts()}][Auth WS] Connected (${socket.id})`);
    };

    // English comment: initial auth context from WS
    socket.on("auth:init", (data) => {
      console.log("[Auth WS] auth:init received", data);
      if (data?.userId) {
        setUserId(data.userId);
      }
    });

    const handleDisconnect = (reason) => {
      setWsConnected(false);
      console.warn(`[${ts()}][Auth WS] Disconnected:`, reason);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    return () => {
      //console.log(`[${ts()}][Auth WS] Cleanup listeners`);
      detach();
      socket.off("auth:init");
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, []);

  // -------------------------------------------------------------
  // connect WS only when isLoggedIn becomes true
  // -------------------------------------------------------------
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    if (isLoggedIn) {
      console.log(`[${ts()}][Auth WS] isLoggedIn=true → connectWs()`);
      connectWs();
    } else {
      console.log(`[${ts()}][Auth WS] isLoggedIn=false → disconnectWs()`);
      disconnectWs();
    }
  }, [isLoggedIn]);

  // English comments only in code blocks
  async function getUserInfo() {
    try {
      const res = await axios.get("/api/user/info", { withCredentials: true });
      const user = res.data || {};
      setUserId(user.userId ?? null);
      setEmail(user.email ?? null);
      setDiscordName(user.discordName ?? null);
      setDiscordId(user.discordId ?? null);
      setHasMfa(user.hasMfa ?? false);
      setMfaType(user.mfaType ?? null);
      setHandles(user.handles ?? null);
      setLanguage(user.language ?? null);
      setTimezone(user.timezone ?? null);
      setLastAcceptanceDate(user.lastAcceptanceDate ?? null);
      setGcuAccepted(user.gcuAccepted ?? false);
      setRankImageUrl(user.rankImageUrl ?? null);
      setRankColors(user.rankColors ?? null);
      setIsAltAccount(Boolean(user.isAltAccount));

      return true;
    } catch (err) {
      console.error("[AuthContext] getUserInfo failed:", err);
      return false;
    }
  }

  const injectAuth = (user) => {
    if (!user) return;
    setUserId(user.userId ?? null);
    setIsLoggedIn(Boolean(user.isLoggedIn));
    setEmail(user.email ?? null);
    setDiscordName(user.discordName ?? null);
    setDiscordId(user.discordId ?? null);
    setHasMfa(user.hasMfa ?? false);
    setMfaType(user.mfaType ?? null);
    setHandles(user.handles ?? null);
    setLanguage(user.language ?? null);
    setTimezone(user.timezone ?? null);
    setLastAcceptanceDate(user.lastAcceptanceDate ?? null);
    setGcuAccepted(user.gcuAccepted ?? false);
    setRankImageUrl(user.rankImageUrl ?? null);
    setRankColors(user.rankColors ?? null);
    setIsAltAccount(Boolean(user.isAltAccount));
  };

  const value = {
    userId,
    isLoggedIn,
    email,
    userId,
    discordName,
    discordId,
    hasMfa,
    mfaType,
    handles,
    language,
    timezone,
    lastAcceptanceDate,
    gcuAccepted,
    rankImageUrl,
    rankColors,
    isAltAccount,

    setUserId,
    setIsLoggedIn,
    setEmail,
    setDiscordName,
    setDiscordId,
    setHasMfa,
    setMfaType,
    setHandles,
    setLanguage,
    setTimezone,
    setLastAcceptanceDate,
    setGcuAccepted,
    setRankImageUrl,
    setRankColors,
    setIsAltAccount,

    injectAuth,

    wsConnected,
    socket: socketRef,
    emit: (...args) => socketRef.current?.emit(...args),

    connectWs,
    disconnectWs,
  };

  useEffect(() => {
  console.log('[AUTH STATE UPDATE]', {
    userId,
    isLoggedIn,
    email,
    discordName,
    discordId,
    hasMfa,
    mfaType,
    handles,
    language,
    timezone,
    lastAcceptanceDate,
    gcuAccepted,
    rankImageUrl,
    rankColors,
    isAltAccount,
  });
}, [
  userId,
  isLoggedIn,
  email,
  discordName,
  discordId,
  hasMfa,
  mfaType,
  handles,
  language,
  timezone,
  lastAcceptanceDate,
  gcuAccepted,
  rankImageUrl,
  rankColors,
  isAltAccount,
]);

  useEffect(() => {
    const onUserUpdate = (e) => {
      const data = e.detail || {};
      console.log('[AuthContext] WS update received:', data);
      if ("userId" in data) setUserId(data.userId ?? null);
      if ("email" in data) setEmail(data.email ?? null);
      if ("discordName" in data) setDiscordName(data.discordName ?? null);
      if ("discordId" in data) setDiscordId(data.discordId ?? null);
      if ("hasMfa" in data) setHasMfa(data.hasMfa ?? false);
      if ("mfaType" in data) setMfaType(data.mfaType ?? null);
      if ("handles" in data) setHandles(data.handles ?? null);
      if ("language" in data) setLanguage(data.language ?? null);
      if ("timezone" in data) setTimezone(data.timezone ?? null);
      if ("lastAcceptanceDate" in data) setLastAcceptanceDate(data.lastAcceptanceDate ?? null);
      if ("gcuAccepted" in data) setGcuAccepted(data.gcuAccepted ?? false);
      if ("rankImageUrl" in data) setRankImageUrl(data.rankImageUrl ?? null);
      if ("rankColors" in data) setRankColors(data.rankColors ?? null);
      if ("isAltAccount" in data) setIsAltAccount(Boolean(data.isAltAccount));
    };

    window.addEventListener("auth-user-update", onUserUpdate);
    return () => window.removeEventListener("auth-user-update", onUserUpdate);
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}